/**
 * What the scope draws over the map: the STARs and approach for the runways in use with their
 * altitude and speed limits, the other procedures dimmed, and the selected aircraft's route ahead.
 * Pure; builds ScopeOverlay data from the airport and the sim's runway configuration.
 */
import { approachLabel, approachPath, defaultApproach, formatRestrictionScope, procedureGeometry, type AirportData, type InstrumentApproach, type Procedure, type RunwayConfig } from '@atc/core';
import type { ScopeOverlay } from '@atc/radar';
import type { PathPoint } from '@atc/sim';

export type ProcedureMode = 'off' | 'active' | 'all';
const MODE_CYCLE: readonly ProcedureMode[] = ['active', 'all', 'off'];
export const nextProcedureMode = (m: ProcedureMode): ProcedureMode => MODE_CYCLE[(MODE_CYCLE.indexOf(m) + 1) % MODE_CYCLE.length]!;

type Label = ScopeOverlay['labels'][number];

function addProcedure(out: ScopeOverlay, labels: Map<string, Label>, airport: AirportData, p: Procedure, rwyKey: string | undefined, active: boolean): void {
  const g = procedureGeometry(airport, p, rwyKey);
  for (const l of g.lines) out.lines.push({ points: l.points, style: !active ? 'dim' : l.emphasis === 'main' ? 'active' : 'transition' });
  if (!active) return;
  for (const f of g.fixes) {
    const text = formatRestrictionScope(f.restriction);
    if (text && !labels.has(f.name)) labels.set(f.name, { at: f.at, lines: [text], style: 'active' });
  }
}

function addApproach(out: ScopeOverlay, labels: Map<string, Label>, airport: AirportData, a: InstrumentApproach, active: boolean): void {
  const path = approachPath(airport, a);
  if (!path) return;
  out.lines.push({ points: [...path.points, path.threshold], style: active ? 'active' : 'dim' });
  if (!active) return;
  out.lines.push({ points: path.missed, style: 'missed' });
  for (const p of path.points) {
    const text = [p.role, formatRestrictionScope(p.restriction)].filter(Boolean).join(' ');
    // On final the approach's fix role and altitude matter more than a STAR's window at the same fix.
    if (text) labels.set(p.name, { at: p, lines: [text], style: 'active', marker: a.source === 'synthesized' });
  }
  labels.set(`threshold ${a.runway}`, { at: path.threshold, lines: [approachLabel(a)], style: 'active' });
}

/** STARs and the approach for the landing runway ("active"), plus every other procedure dimmed ("all"). */
export function procedureOverlay(airport: AirportData, config: RunwayConfig, mode: ProcedureMode): ScopeOverlay | null {
  if (mode === 'off') return null;
  const out: ScopeOverlay = { lines: [], labels: [] };
  const labels = new Map<string, Label>();
  const arrKeys = new Set(config.arrival.runways.map((r) => `${airport.icao}${r}`)), depKeys = new Set(config.departure.runways.map((r) => `${airport.icao}${r}`));
  for (const p of airport.stars.values()) {
    const activeKey = Object.keys(p.rwy).find((k) => arrKeys.has(k));
    if (activeKey) addProcedure(out, labels, airport, p, activeKey, true);
    else if (mode === 'all') addProcedure(out, labels, airport, p, Object.keys(p.rwy)[0], false);
  }
  if (mode === 'all') {
    for (const p of airport.sids.values()) { const key = Object.keys(p.rwy).find((k) => depKeys.has(k)); if (key) addProcedure(out, labels, airport, p, key, false); }
    for (const a of airport.approaches) if (!config.arrival.runways.includes(a.runway)) addApproach(out, labels, airport, a, false);
  }
  for (const runway of config.arrival.runways) { const a = defaultApproach(airport, runway); if (a) addApproach(out, labels, airport, a, true); }
  out.labels = [...labels.values()];
  return out;
}

/** The selected aircraft's route ahead; redrawn every frame because it starts at the aircraft. */
export function routeAheadOverlay(path: readonly PathPoint[]): ScopeOverlay | null {
  return path.length >= 2 ? { lines: [{ points: [...path], style: 'route' }], labels: [], live: true } : null;
}

/** Header chip: "ARR 12 ILS · DEP 26", with wind components in the tooltip and a flag over the tailwind limit. */
export function runwayChip(config: RunwayConfig): { text: string; title: string; over: boolean } {
  const a = config.arrival.approach;
  const type = a ? (a.type === 'visual' ? 'VIS' : a.type.toUpperCase()) : '';
  const wind = (kt: number) => (kt > 0.5 ? `${Math.round(kt)} kt tailwind` : `${Math.round(Math.max(0, -kt))} kt headwind`);
  const over = config.overLimit.arrival || config.overLimit.departure;
  return {
    text: `ARR ${config.arrival.runways.join('/')}${config.arrival.runways.length === 1 && type ? ` ${type}` : ''} · DEP ${config.departure.runways.join('/')}`,
    title: `Landing runway${config.arrival.runways.length > 1 ? 's' : ''} ${config.arrival.runways.join(', ')}${a && config.arrival.runways.length === 1 ? ` (${a.name}${a.source === 'synthesized' ? ', synthesized' : ''})` : ''}, ${wind(config.tailwindKt.arrival)}. Departing runway${config.departure.runways.length > 1 ? 's' : ''} ${config.departure.runways.join(', ')}, ${wind(config.tailwindKt.departure)}.${over ? ' Tailwind is over the published limit.' : ''} Click for the briefing.`,
    over,
  };
}
